/* eslint-disable max-lines */
/* eslint-disable sonarjs/cognitive-complexity */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import { scrollToBreadCrumb, isClient, getAPIConfig, getViewportInfo } from '@tcp/core/src/utils';
import useHasMounted from '@tcp/core/src/hooks/useHasMounted';
import withLazyLoad from '@tcp/core/src/components/common/hoc/withLazyLoad';
import { Heading } from '@tcp/core/styles/themes/TCP/typotheme';
import CategoryPills from '@tcp/core/src/components/features/browse/ProductListing/molecules/CategoryPills';
import ToastModal from '@tcp/core/src/components/features/account/common/molecule/ToastModal/index';
import SkeletonLoader from '@tcp/core/src/components/common/molecules/LoaderSkelton';
import { v4 as uuidv4 } from 'uuid';
import pushEventToAdobeAnalytics from '@tcp/core/src/adobeAnalytics/adobe.utils';
import FilterPillsModule from '@tcp/core/src/components/features/browse/QuickFilters/molecules/FilterPills/views/FilterPills.view.jsx';
import { getLabelFromCopyOrFromTranslation } from '@tcp/core/src/utils/labels/labelUtilty';
import { PLPLabels } from '@tcp/core/src/utils/labels/browse/PLP.labels';
import withStyles from '../../../../common/hoc/withStyles';
import styles, {
  HorizontalBar,
  StyledWrapper,
  StyledSortSkelton,
  StyledPaginatedSkeleton,
  L2TitleSkeleton,
  L2TitleWrapper,
  SortAndFilterWrapper,
  SortByComponentWrapper,
} from '../styles/PaginatedPage.style';
import EmptyCategoryPageView from '../molecules/EmptyCategoryPage/views/EmptyCategoryPage.view';
import {
  calculateSkeletonCount,
  setUpdatedUrl,
  getPlpUrlQueryValuesSeo,
  getFieldFromNavigation,
} from '../container/CategoryListingPage.utils';
import PaginatedView from './components/PaginatedView';
import CategoryModuleFilter from '../molecules/CategoryModuleFilter/views/CategoryModuleFilter';
import FilterIcon from './PaginatedPageIcons.view';
import QuickFilters from '../../QuickFilters/views/QuickFilters.view';

const SortByComponent = dynamic(
  () =>
    import(
      // eslint-disable-next-line extra-rules/no-commented-out-code
      /* webpackChunkName: "category-filters" */ '../molecules/SortWrapper/views/SortWrapper.view'
    ),
  {
    ssr: false,
    loading: () => {
      return (
        <StyledWrapper>
          <StyledSortSkelton />
        </StyledWrapper>
      );
    },
  }
);

const PaginatorComponent = dynamic(
  () =>
    import(
      // eslint-disable-next-line extra-rules/no-commented-out-code
      /* webpackChunkName: "paginator" */ '@tcp/core/src/components/common/molecules/Paginator'
    ),
  {
    ssr: false,
  }
);

const PlpPaginatorComponent = dynamic(
  () =>
    import(
      // eslint-disable-next-line extra-rules/no-commented-out-code
      /* webpackChunkName: "new-plp-paginator" */ '@tcp/core/src/components/common/molecules/PlpPaginator'
    ),
  {
    ssr: false,
  }
);

const plpShowingResultpaginator = dynamic(
  () =>
    import(
      // eslint-disable-next-line extra-rules/no-commented-out-code
      /* webpackChunkName: "new-plp-paginator" */ '@tcp/core/src/components/common/molecules/plpShowingResultPaginator'
    ),
  {
    ssr: false,
  }
);

const oldPaginator = withLazyLoad(PaginatorComponent);
const newPaginator = withLazyLoad(PlpPaginatorComponent);
const newPlpShowingResultPaginator = withLazyLoad(plpShowingResultpaginator);

export const insertTitleGrid = (paginatedPLP, tileList = []) => {
  const updatedPPLP = paginatedPLP;
  tileList.forEach((title) => {
    const { titleIndex, id, name } = title;
    updatedPPLP[titleIndex] = (
      <Heading key={id} className="item-title" fontFamily="secondaryFontFamily">
        {name}
        <HorizontalBar />
      </Heading>
    );
  });
  return updatedPPLP;
};

const getSkeletonHero = (key) => {
  return (
    <div key={key} className="hero-tile-container hide-hero-tile skelton-hero-tile">
      <SkeletonLoader className="image-height" />
      <SkeletonLoader height="20px" width="150px" />
      <SkeletonLoader height="20px" />
      <SkeletonLoader height="20px" />
    </div>
  );
};

function generateDummyUpdatedPPLP(length, columns, rowsWithHero = []) {
  const updatedPPLP = Array.from({ length }, (_, i) => <StyledPaginatedSkeleton key={i} />);
  let gridItem = 1;
  let itemRow = 1;
  const updatedPPLPIndex = [];
  updatedPPLP.forEach((item, ind) => {
    gridItem += 1;
    if (gridItem > 1 && gridItem % columns === 1) itemRow += 1;
    if (rowsWithHero.includes(itemRow)) {
      rowsWithHero.shift();
      updatedPPLPIndex.push(ind);
    }
  });

  // 4. Insert hero tiles at identified row indexes
  updatedPPLPIndex.forEach((ind) => {
    const items = Array.from({ length: 2 }, () => getSkeletonHero(`hero-1-row-${uuidv4()}`));
    let insertIndex = ind;

    items.forEach((heroSkeleton) => {
      updatedPPLP.splice(
        insertIndex,
        0,
        heroSkeleton,
        <div className="is-hidden" key={`empty-div-skeleton-${uuidv4()}`} />
      );

      insertIndex += 2;
    });
  });

  return updatedPPLP;
}

export const PaginatedPlpSkeleton = (
  isPaginatedPlpLoading,
  isPaginatorClicked,
  recordsPerPage,
  shouldRenderSkeletonComponent,
  isShowL2Skeleton,
  totalProductGrids,
  heroSlot,
  gridColumn
) => {
  const showSkeletonPLP =
    isPaginatedPlpLoading ||
    isPaginatorClicked ||
    isShowL2Skeleton ||
    !totalProductGrids ||
    shouldRenderSkeletonComponent;
  if (showSkeletonPLP && heroSlot && gridColumn) {
    return generateDummyUpdatedPPLP(recordsPerPage, gridColumn, heroSlot);
  }
  return showSkeletonPLP
    ? [...Array(recordsPerPage)].map((_, key) => <StyledPaginatedSkeleton key={key} />)
    : null;
};

const L2CategorySkeleton = (
  isPaginatedPlpLoading,
  isL2Category,
  isInitialCall,
  isShowL2Skeleton
) => {
  return isPaginatedPlpLoading && isL2Category && isInitialCall && isShowL2Skeleton ? (
    <L2TitleWrapper>
      <L2TitleSkeleton />
      <HorizontalBar />
    </L2TitleWrapper>
  ) : null;
};

const getGridColumn = () => {
  if (typeof window === 'undefined') return 4; // SSR
  const width = window.innerWidth;
  const isThreeColumn = width > 534 && width < 930;

  if (isThreeColumn) return null;

  const { isMobile } = getViewportInfo();
  return isMobile ? 2 : 4;
};

// eslint-disable-next-line complexity
const PaginatedPage = (props) => {
  const {
    paginationObj,
    totalProductsCount,
    recordsPerPage,
    paginatedProducts,
    className,
    plpGridPromos,
    plpCrossPromos,
    submitSortChange,
    navTree,
    processedBreadCrumbs,
    isBopisFilterOn,
    isPaginatedPlpLoading,
    isInitialCall,
    isL2Category,
    pageNumber,
    pathName,
    setUpdatedUrlActn,
    isShowL2Skeleton,
    isServiceError,
    isNewPlpPaginatorEnabled,
    isPLPCategoryPillsEnabled,
    isNewPlpPaginatorShowingResultEnabled,
    currentNavIds,
    trackCategoryPillClick,
    isPaginatedAlternatePlpEnabled,
    trackPaginationClick,
    isNewNavigationImagesEnabled,
    sortByRatingLabel,
    isMostFavoritedBadgeEnabled,
    setPLPSLPAddToCartCTA,
    isTileRedesignAB,
    favAddRemoveLoadingState,
    toastModalState,
    showToastModalAction,
    isAlternateImageOnHoverEnabled,
    shouldRenderSkeletonComponent,
    isOneHrFilterEnabled,
    imageConfigWithoutDPR,
    isFacetedNavigationEnabled,
    seoUrlPayload,
    isStageHoldCategoryOosMessage,
    isFullWidthInGridPromoHidden,
    isDynamicColorSwatchesEnabled,
    isHoverToPreviewColorSwatchesEnabled,
    isInteractiveColorSwatchBandEnabled,
    isHoverToRevealATBButtonEnabled,
    isFacetedNavigationNoIndexApplicableCheck,
    isFourTileGridEnabled,
    isSwatchAsColorUrlEnabled,
    categoryModuleFilters,
    updateBopisFormValue,
    appliedFilters,
    newFormData,
    setPriceFilterFlagActn,
    isQuickFiltersModuleEnabled,
    filterMaps,
    isPLPDynamicGridHeroEnabled,
    isNewPLPAtbCardTileEnabled,
    hideFilters,
    setHideFilters,
    isMountedHideLeftNavigationOnPLPEnabled,
    isQuickFiltersEnabled,
    quickFilters,
    isPLPSortAndOneHrFilterRedesignEnabled,
    hidePromoOnFilterApplied,
    isDynamicBadgeEnabledAbtest,
    isFadeImageHoverEnabled,
    isTransparentImageSizeReduceEnabled,
    addToDefaultFavoritesEnabled,
  } = props;
  const [currentPageNumber, setCurrentPageNumber] = useState(pageNumber);
  const [isPaginatorClicked, setIsPaginatorClicked] = useState(false);
  const [numOfColumns, setNumOfColumns] = useState(null);
  const [numOfProductsInCBC, setNumOfProductsInCBC] = useState(null);
  const [, setDomNode] = useState(null);
  const [pathNameWithoutQuery, urlQueryValues] = pathName.split('?');
  const [, , pageType, cid] = pathNameWithoutQuery.split('/');
  const hasMounted = useHasMounted();
  const { isMobile, isDesktop } = getViewportInfo() || {};
  const showFiltersLabel = getLabelFromCopyOrFromTranslation(
    'PLP.PLP_sort_filter.lbl_show_filters',
    PLPLabels
  );
  const hideFiltersLabel = getLabelFromCopyOrFromTranslation(
    'PLP.PLP_sort_filter.lbl_hide_filters',
    PLPLabels
  );

  const defaultColumnCount = isFourTileGridEnabled ? 4 : 5;

  const totalProductGrids = paginatedProducts?.length;
  const gridRef = useRef();
  const newPlpPaginator = isNewPlpPaginatorShowingResultEnabled
    ? newPlpShowingResultPaginator
    : newPaginator;
  const Paginator = isNewPlpPaginatorEnabled ? newPlpPaginator : oldPaginator;
  const gridtemplateColumns = 'grid-template-columns';
  const calculateGridAttributes = () => {
    if (plpCrossPromos?.length && isClient() && gridRef?.current) {
      const gridComputedStyle = window.getComputedStyle(gridRef.current);
      // get number of grid columns
      const gridColumnCount = gridComputedStyle
        .getPropertyValue(gridtemplateColumns)
        .split(' ').length;
      let gridWidth = gridComputedStyle.getPropertyValue('width');
      if (gridWidth) {
        [gridWidth] = gridWidth.split('px');
        gridWidth = gridWidth && parseInt(gridWidth, 10);
        const CBC_LEFT_IMAGE_WIDTH = 466;
        const CBC_PRODUCT_TILE_WIDTH = 391;
        gridWidth -= CBC_LEFT_IMAGE_WIDTH;
        const numberOfTiles = Math.floor(gridWidth / CBC_PRODUCT_TILE_WIDTH);
        setNumOfProductsInCBC(numberOfTiles);
      }
      setNumOfColumns(gridColumnCount);
    }
  };

  useEffect(() => {
    calculateGridAttributes();
  }, [gridRef?.current, plpCrossPromos]);

  useEffect(() => {
    if (pageNumber !== currentPageNumber) setCurrentPageNumber(pageNumber);
    setIsPaginatorClicked(false);
  }, [pageNumber]);

  const pageClick = (page, skipUrlUpdate) => {
    if (page >= 1 && page <= paginationObj?.length && page !== currentPageNumber) {
      const { formDetails, asPath, seoFacetedNavigationConfig, clothingSizeObj } = seoUrlPayload;
      setIsPaginatorClicked(true);
      setCurrentPageNumber(page);
      if (!skipUrlUpdate) {
        const updatedUrl = isFacetedNavigationEnabled
          ? getPlpUrlQueryValuesSeo(
              formDetails,
              asPath,
              true,
              page,
              seoFacetedNavigationConfig,
              clothingSizeObj
            )
          : setUpdatedUrl(pageType, cid, urlQueryValues, page);
        setUpdatedUrlActn({ updatedUrl });
      }
      setTimeout(() => {
        scrollToBreadCrumb();
      }, 10);
    }
  };

  function nearestSlot(slotNum, mod) {
    if (mod > defaultColumnCount) {
      return slotNum + (mod - (slotNum % mod));
    }
    const modVal = slotNum % mod;
    if (modVal > slotNum) {
      return slotNum + modVal;
    }
    return slotNum - modVal;
  }
  const getPromoSlot = (plpPromo, noc) => {
    const [, position, isDouble] = plpPromo?.slot?.split('_');
    const hasDouble = isDouble === 'double';
    const isFullWidth = isDouble === 'fullwidth';
    if (isDouble && !isFullWidth) {
      return { position: position % noc === 0 ? position - 1 : position, hasDouble };
    }
    return !isFullWidth && { position, hasDouble };
  };

  const getGridPromoRow = (plpPromo) => {
    const slotParts = plpPromo?.slot?.split('_');

    if (!slotParts || slotParts.length < 2) {
      return {}; // invalid slot format
    }
    const promoRow = slotParts?.[1];
    if (!promoRow) {
      return {}; // promoRow is null or undefined
    }

    const isFullwidth = slotParts.includes('fullwidth');
    if (!isFullwidth) {
      return {}; // not a fullwidth promo
    }

    const isMobileSlot = slotParts.includes('mb');
    const isDesktopSlot = !isMobileSlot;

    if ((isMobile && isMobileSlot) || (!isMobile && isDesktopSlot)) {
      return { promoRow, isFullWidth: true };
    }
    return {};
  };
  const min = (currentPageNumber - 1) * recordsPerPage;
  const max = currentPageNumber * recordsPerPage;
  const gridExist = gridRef?.current;

  const onRefChange = useCallback((node) => {
    gridRef.current = node;
    setDomNode(node);
  }, []);

  const currentGridNo = useMemo(() => {
    let gridColumnCount = defaultColumnCount;
    if (isClient() && gridExist) {
      const gridComputedStyle = window && window.getComputedStyle(gridRef.current);
      gridColumnCount = gridComputedStyle?.getPropertyValue(gridtemplateColumns).split(' ').length;
    }
    return plpGridPromos
      ? plpGridPromos.reduce((acc, plpPromo) => {
          const { position, hasDouble } = getPromoSlot(plpPromo, gridColumnCount);
          if (position > min && position <= max) {
            acc[position] = { tileSize: hasDouble ? 2 : 1, ...plpPromo };
          }
          return acc;
        }, {})
      : null;
  }, [plpGridPromos, min, gridExist]);

  const gridColumnCount = useMemo(() => {
    if (isClient() && gridExist) {
      const gridComputedStyle = window && window.getComputedStyle(gridRef.current);
      return gridComputedStyle?.getPropertyValue(gridtemplateColumns).split(' ').length;
    }
    return defaultColumnCount; // default value
  }, [gridExist, gridRef, isClient]);

  const currentRowNo = useMemo(() => {
    if (!plpGridPromos) return null;

    return plpGridPromos.reduce((acc, plpPromo) => {
      const { promoRow, isFullWidth } = getGridPromoRow(plpPromo);
      const promoPosition = (parseInt(promoRow, 10) - 1) * gridColumnCount;
      if (promoPosition > min && promoPosition <= max && isFullWidth) {
        acc[promoPosition] = { tileSize: gridColumnCount, ...plpPromo };
      }
      return acc;
    }, {});
  }, [plpGridPromos, gridColumnCount, min, max]);

  const currentCrossGridNo = useMemo(() => {
    return plpCrossPromos?.length && numOfColumns
      ? plpCrossPromos.reduce((acc, plpPromo, idx) => {
          const [, position] = plpPromo?.slot?.split('_');
          let slotNum = parseInt(position, 10);
          // eslint-disable-next-line sonarjs/no-collapsible-if
          if (position > min && position <= max) {
            if (isClient()) {
              slotNum = nearestSlot(slotNum, parseInt(numOfColumns, 10));
              acc[slotNum + 1 + idx] = { ...plpPromo, numberOfProducts: numOfProductsInCBC };
            }
          }
          return acc;
        }, {})
      : null;
  }, [plpCrossPromos, min, numOfColumns]);

  /**
   * The cls issue caused because we fetch limited products on server side
   * @returns number of skeleton to be rendered on server side to solve cls issue
   */
  const getAdditionalProductsCountOnCsr = () => {
    const apiConfig = getAPIConfig();
    const { firstLoadPLPProductCount = 8 } = apiConfig;
    if (isInitialCall) {
      return calculateSkeletonCount(
        totalProductsCount,
        recordsPerPage,
        firstLoadPLPProductCount,
        pathName
      );
    }
    return 0;
  };

  const handleShowHideFiltersClick = () => {
    requestAnimationFrame(() => {
      setHideFilters(!hideFilters);
      pushEventToAdobeAnalytics('show_hide_filters_click', { hideFilters });
    });
  };

  // Determine the class for the PLP grid
  let plpGridClass = 'plp-grid';

  if (isPaginatedAlternatePlpEnabled) {
    plpGridClass += ' alternate-plp';
  } else if (isFourTileGridEnabled) {
    plpGridClass += ' four-grid';
  }
  const { formDetails, seoFacetedNavigationConfig, clothingSizeObj } = seoUrlPayload || {};
  let heroSlot = null;
  let gridColumn = null;
  if (
    isPLPDynamicGridHeroEnabled &&
    !isPaginatedAlternatePlpEnabled &&
    !isNewPLPAtbCardTileEnabled
  ) {
    if (isFourTileGridEnabled) {
      gridColumn = !hasMounted ? 4 : getGridColumn();
    }
    const categorySlot = getFieldFromNavigation(navTree, currentNavIds, 'heroCategorySlotting');
    const row = categorySlot?.split('|')?.map(Number);
    if (row?.length > 0) heroSlot = row;
  }

  const shouldShowEmptyCategory =
    hasMounted &&
    ((totalProductsCount === 0 && !isPaginatedPlpLoading) || isStageHoldCategoryOosMessage);

  const shouldShowCategoryModuleFilter =
    !shouldShowEmptyCategory && isQuickFiltersModuleEnabled && isDesktop;

  return (
    <div className={className}>
      {shouldShowCategoryModuleFilter && (
        <CategoryModuleFilter
          categoryModuleFilters={categoryModuleFilters}
          updateBopisFormValue={updateBopisFormValue}
          pathName={pathName}
          formDetails={formDetails}
          isFacetedNavigationEnabled={isFacetedNavigationEnabled}
          seoFacetedNavigationConfig={seoFacetedNavigationConfig}
          cleanUrlObj={clothingSizeObj}
          setUpdatedUrlActn={setUpdatedUrlActn}
          appliedFilters={appliedFilters}
          isLoadingMore={isPaginatedPlpLoading}
          newFormData={newFormData}
          setPriceFilterFlagActn={setPriceFilterFlagActn}
          filterMaps={filterMaps}
          isHideAppliedFilters
          isPLPSortAndOneHrFilterRedesignEnabled={isPLPSortAndOneHrFilterRedesignEnabled}
        />
      )}
      <SortAndFilterWrapper
        isMountedHideLeftNavigationOnPLPEnabled={isMountedHideLeftNavigationOnPLPEnabled}
        isAppliedFilterPill={
          (isQuickFiltersModuleEnabled || isQuickFiltersEnabled) &&
          (!isEmpty(quickFilters) || !isEmpty(categoryModuleFilters))
        }
        isQuickFiltersEnabled={isQuickFiltersEnabled && !isEmpty(quickFilters)}
        isPLPSortAndOneHrFilterRedesignEnabled={isPLPSortAndOneHrFilterRedesignEnabled}
        className={isPLPSortAndOneHrFilterRedesignEnabled ? 'sort-and-filter-redesign' : ''}
      >
        {isPLPSortAndOneHrFilterRedesignEnabled ? (
          <>
            {isMountedHideLeftNavigationOnPLPEnabled && (
              <>
                <button onClick={handleShowHideFiltersClick} className="show-hide-filters-button">
                  <FilterIcon />
                  {hideFilters ? showFiltersLabel : hideFiltersLabel}
                </button>
              </>
            )}
            <SortByComponentWrapper
              className="sort-by-component-wrapper"
              isMountedHideLeftNavigationOnPLPEnabled={isMountedHideLeftNavigationOnPLPEnabled}
              isPLPSortAndOneHrFilterRedesignEnabled={isPLPSortAndOneHrFilterRedesignEnabled}
            >
              <SortByComponent
                submitSortChange={submitSortChange}
                totalProductsCount={totalProductsCount === -1 ? 0 : totalProductsCount}
                sortByRatingLabel={sortByRatingLabel}
                pathName={pathName}
                isOneHrFilterEnabled={isOneHrFilterEnabled}
                isPlp
                isQuickFilters={isQuickFiltersEnabled}
                hideOneHrFilter
                isPLPSortAndOneHrFilterRedesignEnabled={isPLPSortAndOneHrFilterRedesignEnabled}
              />
            </SortByComponentWrapper>
            {isQuickFiltersEnabled && (
              <QuickFilters
                filterMaps={filterMaps}
                setUpdatedUrlActn={setUpdatedUrlActn}
                isFacetedNavigationEnabled={isFacetedNavigationEnabled}
                seoFacetedNavigationConfig={seoFacetedNavigationConfig}
                cleanUrlObj={clothingSizeObj}
                pathName={pathName}
                formDetails={formDetails}
                appliedFilters={appliedFilters}
                updateBopisFormValue={updateBopisFormValue}
                setPriceFilterFlagActn={setPriceFilterFlagActn}
                isPLPSortAndOneHrFilterRedesignEnabled={isPLPSortAndOneHrFilterRedesignEnabled}
              />
            )}
            <SortByComponentWrapper
              className="sort-by-component-wrapper one-hr-filter-container"
              isMountedHideLeftNavigationOnPLPEnabled={isMountedHideLeftNavigationOnPLPEnabled}
              isPLPSortAndOneHrFilterRedesignEnabled={isPLPSortAndOneHrFilterRedesignEnabled}
            >
              <SortByComponent
                submitSortChange={submitSortChange}
                totalProductsCount={totalProductsCount === -1 ? 0 : totalProductsCount}
                sortByRatingLabel={sortByRatingLabel}
                pathName={pathName}
                isOneHrFilterEnabled={isOneHrFilterEnabled}
                isPlp
                isQuickFilters={isQuickFiltersEnabled}
                hideSort
                isPLPSortAndOneHrFilterRedesignEnabled={isPLPSortAndOneHrFilterRedesignEnabled}
              />
            </SortByComponentWrapper>
          </>
        ) : (
          <>
            {isMountedHideLeftNavigationOnPLPEnabled && (
              <>
                <button onClick={handleShowHideFiltersClick} className="show-hide-filters-button">
                  <FilterIcon />
                  {hideFilters ? showFiltersLabel : hideFiltersLabel}
                </button>
              </>
            )}
            {isQuickFiltersEnabled && (
              <QuickFilters
                filterMaps={filterMaps}
                setUpdatedUrlActn={setUpdatedUrlActn}
                isFacetedNavigationEnabled={isFacetedNavigationEnabled}
                seoFacetedNavigationConfig={seoFacetedNavigationConfig}
                cleanUrlObj={clothingSizeObj}
                pathName={pathName}
                formDetails={formDetails}
                appliedFilters={appliedFilters}
                updateBopisFormValue={updateBopisFormValue}
                setPriceFilterFlagActn={setPriceFilterFlagActn}
              />
            )}
            <SortByComponentWrapper
              className="sort-by-component-wrapper"
              isMountedHideLeftNavigationOnPLPEnabled={isMountedHideLeftNavigationOnPLPEnabled}
            >
              <SortByComponent
                submitSortChange={submitSortChange}
                totalProductsCount={totalProductsCount === -1 ? 0 : totalProductsCount}
                sortByRatingLabel={sortByRatingLabel}
                pathName={pathName}
                isOneHrFilterEnabled={isOneHrFilterEnabled}
                isPlp
                isQuickFilters={isQuickFiltersEnabled}
              />
            </SortByComponentWrapper>
          </>
        )}
      </SortAndFilterWrapper>
      {(isQuickFiltersModuleEnabled || isQuickFiltersEnabled) &&
        (!isEmpty(quickFilters) || !isEmpty(categoryModuleFilters)) && (
          <FilterPillsModule
            filtersApplied={appliedFilters}
            filterMaps={filterMaps}
            isLoadingMore={isPaginatedPlpLoading}
            updateBopisFormValue={updateBopisFormValue}
            isFacetedNavigationEnabled={isFacetedNavigationEnabled}
            pathName={pathName}
            seoFacetedNavigationConfig={seoFacetedNavigationConfig}
            cleanUrlObj={clothingSizeObj}
            formDetails={formDetails}
            setPriceFilterFlagActn={setPriceFilterFlagActn}
            setUpdatedUrlActn={setUpdatedUrlActn}
            categoryModuleFilters={categoryModuleFilters}
          />
        )}
      {shouldShowEmptyCategory && (
        <EmptyCategoryPageView
          pathName={pathName}
          navTree={navTree}
          breadCrumbs={processedBreadCrumbs}
          isBopisFilterOn={isBopisFilterOn}
          isServiceError={isServiceError}
          isPlp
          isPLPSortAndOneHrFilterRedesignEnabled={isPLPSortAndOneHrFilterRedesignEnabled}
        />
      )}
      {(totalProductsCount > 0 || isPaginatedPlpLoading) && !isStageHoldCategoryOosMessage && (
        <>
          <div
            className={`${plpGridClass} ${
              isPLPSortAndOneHrFilterRedesignEnabled ? 'plp-grid-redesign' : ''
            }`}
            ref={onRefChange}
          >
            {L2CategorySkeleton(
              isPaginatedPlpLoading,
              isL2Category,
              isInitialCall,
              isShowL2Skeleton
            )}
            {isPaginatorClicked &&
              PaginatedPlpSkeleton(isPaginatedPlpLoading, isPaginatorClicked, recordsPerPage)}

            {!shouldRenderSkeletonComponent && totalProductGrids ? (
              <PaginatedView
                {...props}
                additionalProductsCountOnCsr={getAdditionalProductsCountOnCsr()}
                min={min}
                currentGridNo={currentGridNo}
                currentRowNo={currentRowNo}
                gridColumnCount={gridColumnCount}
                currentCrossGridNo={currentCrossGridNo}
                currentPageNumber={currentPageNumber}
                isPaginatorClicked={isPaginatorClicked}
                isMostFavoritedBadgeEnabled={isMostFavoritedBadgeEnabled}
                setPLPSLPAddToCartCTA={setPLPSLPAddToCartCTA}
                cid={cid}
                isTileRedesignAB={isTileRedesignAB}
                favAddRemoveLoadingState={favAddRemoveLoadingState}
                isAlternateImageOnHoverEnabled={isAlternateImageOnHoverEnabled}
                imageConfigWithoutDPR={imageConfigWithoutDPR}
                isFullWidthInGridPromoHidden={isFullWidthInGridPromoHidden}
                isDynamicColorSwatchesEnabled={isDynamicColorSwatchesEnabled}
                isHoverToPreviewColorSwatchesEnabled={isHoverToPreviewColorSwatchesEnabled}
                isInteractiveColorSwatchBandEnabled={isInteractiveColorSwatchBandEnabled}
                isHoverToRevealATBButtonEnabled={isHoverToRevealATBButtonEnabled}
                gridBlockRef={gridRef?.current}
                isSwatchAsColorUrlEnabled={isSwatchAsColorUrlEnabled}
                heroSlot={heroSlot}
                gridColumn={gridColumn}
                hidePromoOnFilterApplied={hidePromoOnFilterApplied}
                isDynamicBadgeEnabledAbtest={isDynamicBadgeEnabledAbtest}
                isFadeImageHoverEnabled={isFadeImageHoverEnabled}
                isTransparentImageSizeReduceEnabled={isTransparentImageSizeReduceEnabled}
                addToDefaultFavoritesEnabled={addToDefaultFavoritesEnabled}
              />
            ) : (
              <StyledPaginatedSkeleton />
            )}
            {!isPaginatorClicked &&
              PaginatedPlpSkeleton(
                isPaginatedPlpLoading,
                false,
                recordsPerPage,
                shouldRenderSkeletonComponent,
                isShowL2Skeleton,
                totalProductGrids,
                heroSlot,
                gridColumn
              )}
          </div>
          <Paginator
            className=""
            currentPage={currentPageNumber}
            totalCount={totalProductsCount === -1 ? 0 : totalProductsCount}
            pageSize={recordsPerPage}
            onPageChange={pageClick}
            trackClick={trackPaginationClick}
            processedBreadCrumbs={processedBreadCrumbs}
            isNewNavigationImagesEnabled={isNewNavigationImagesEnabled}
            isFacetedNavigationNoIndexApplicableCheck={isFacetedNavigationNoIndexApplicableCheck}
            pathName={pathName}
            cid={cid}
            setUpdatedUrlActn={setUpdatedUrlActn}
          />
        </>
      )}
      {isPLPCategoryPillsEnabled && navTree && Object.keys(navTree)?.length !== 0 && (
        <CategoryPills
          className={className}
          navigation={navTree}
          level={currentNavIds}
          pathName={pathName}
          onClick={trackCategoryPillClick}
        />
      )}
      {toastModalState?.showToast && (
        <ToastModal
          className={className}
          duration={toastModalState?.duration}
          showToastModalAction={showToastModalAction}
        />
      )}
    </div>
  );
};

PaginatedPage.propTypes = {
  pageNumber: PropTypes.number,
  paginationObj: PropTypes.arrayOf(),
  totalProductsCount: PropTypes.number,
  recordsPerPage: PropTypes.number,
  paginatedProducts: PropTypes.arrayOf(),
  onQuickViewOpenClick: PropTypes.func,
  onAddItemToFavorites: PropTypes.func,
  onSetLastDeletedItemIdAction: PropTypes.func,
  wishListData: PropTypes.shape({}),
  plpGridPromos: PropTypes.arrayOf(),
  isBrierleyPromoEnabled: PropTypes.bool,
  isShortImage: PropTypes.bool,
  submitSortChange: PropTypes.func,
  saveProductDataForPdp: PropTypes.func,
  navTree: PropTypes.shape({}),
  isBopisFilterOn: PropTypes.bool,
  processedBreadCrumbs: PropTypes.arrayOf(),
  isPaginatedPlpLoading: PropTypes.bool,
  isInitialCall: PropTypes.bool,
  isL2Category: PropTypes.bool,
  pathName: PropTypes.string,
  setUpdatedUrlActn: PropTypes.func,
  isPlcc: PropTypes.bool,
  isInternationalShipping: PropTypes.bool.isRequired,
  isShowL2Skeleton: PropTypes.bool,
  isTopRatedBackAbEnabled: PropTypes.bool,
  isBestSellerBackAbEnabled: PropTypes.bool,
  isNewPLPAtbCardTileEnabled: PropTypes.bool,
  isAlternatePlpEnabled: PropTypes.bool,
  isPaginatedAlternatePlpEnabled: PropTypes.bool,
  isNewSwatchUrlEnabled: PropTypes.bool,
  currentNavIds: PropTypes.arrayOf(),
  isOneClickATCEnabled: PropTypes.bool,
  addToBagEcom: PropTypes.func,
  isTileRedesignAB: PropTypes.bool,
  isAlternateImageOnHoverEnabled: PropTypes.bool,
  isSwatchAsColorUrlEnabled: PropTypes.bool,
};

PaginatedPage.defaultProps = {
  pageNumber: 1,
  paginationObj: [],
  totalProductsCount: -1,
  recordsPerPage: 30,
  paginatedProducts: [],
  onQuickViewOpenClick: () => {},
  onAddItemToFavorites: () => {},
  onSetLastDeletedItemIdAction: () => {},
  wishListData: {},
  plpGridPromos: [],
  isBrierleyPromoEnabled: false,
  isShortImage: false,
  submitSortChange: () => {},
  saveProductDataForPdp: () => {},
  navTree: {},
  isBopisFilterOn: false,
  processedBreadCrumbs: [],
  isPaginatedPlpLoading: false,
  isInitialCall: false,
  isL2Category: false,
  pathName: '',
  setUpdatedUrlActn: () => {},
  isPlcc: false,
  isShowL2Skeleton: false,
  isTopRatedBackAbEnabled: false,
  isNewSwatchUrlEnabled: false,
  isBestSellerBackAbEnabled: false,
  isNewPLPAtbCardTileEnabled: false,
  currentNavIds: [],
  isAlternatePlpEnabled: false,
  isPaginatedAlternatePlpEnabled: false,
  isOneClickATCEnabled: false,
  addToBagEcom: () => {},
  isTileRedesignAB: false,
  isAlternateImageOnHoverEnabled: false,
  isSwatchAsColorUrlEnabled: false,
};

export { PaginatedPage as PaginatedPageVanilla };
export default withStyles(PaginatedPage, styles);
